import { Wallet } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  ProgressMeter,
  StatCard,
  StatCardRow,
} from '@/modules/common/components/molecules/stat-card';

import { formatApprovalMoney } from '../../../helpers/format-approval-money.helper';
import { ApprovalsSummary } from '../../../types/time-tracking-approvals';
import { SummaryChip } from '../shared/summary-chip';
import { SummaryTooltipRows } from '../shared/summary-tooltip-rows';

interface IncomeCardProps {
  summary: ApprovalsSummary;
}

/**
 * Client revenue, so it follows the approved-for-INVOICE hours. There is deliberately no
 * expense counterpart — that would expose employee cost.
 */
const useIncome = (summary: ApprovalsSummary) => {
  const approved = summary.approvedIncome ?? 0;
  const pending = summary.pendingIncome ?? 0;
  const total = approved + pending;

  return {
    approved,
    pending,
    total,
    format: (amount: number) => formatApprovalMoney(amount, summary.currencyCode),
  };
};

/** True when the viewer may see money AND the period has any to show. */
export const hasIncomeToShow = (summary: ApprovalsSummary, showIncomeCard: boolean): boolean =>
  showIncomeCard && (summary.approvedIncome !== null || summary.pendingIncome !== null);

const useIncomeTooltip = (summary: ApprovalsSummary) => {
  const { t } = useTranslation('authenticated.timeTracking');
  const { approved, pending, format } = useIncome(summary);

  return (
    <SummaryTooltipRows
      title={t('approvals.summaryCards.income.title')}
      rows={[
        { label: t('approvals.summaryCards.income.approvedLabel'), value: format(approved) },
        { label: t('approvals.summaryCards.income.pendingLabel'), value: format(pending) },
      ]}
      footer={t('approvals.summaryCards.income.conversionNote', {
        currency: summary.currencyCode ?? '',
      })}
    />
  );
};

export const IncomeCard = ({ summary }: IncomeCardProps) => {
  const { t } = useTranslation('authenticated.timeTracking');
  const { approved, pending, total, format } = useIncome(summary);

  return (
    <StatCard
      icon={<Wallet size={16} aria-hidden />}
      title={t('approvals.summaryCards.income.title')}
      infoTooltip={t('approvals.summaryCards.income.conversionNote', {
        currency: summary.currencyCode ?? '',
      })}
    >
      <StatCardRow
        label={t('approvals.summaryCards.income.approvedLabel')}
        value={
          <span className="whitespace-nowrap text-base font-semibold tabular-nums">
            {format(approved)}
          </span>
        }
      />
      <StatCardRow
        label={t('approvals.summaryCards.income.pendingLabel')}
        value={<span className="whitespace-nowrap tabular-nums">{format(pending)}</span>}
      />
      <ProgressMeter
        progress={total > 0 ? approved / total : null}
        filled={{
          label: t('approvals.summaryCards.income.approvedShortLabel'),
          amount: approved,
        }}
        track={{ label: t('approvals.summaryCards.income.pendingShortLabel'), amount: pending }}
        formatAmount={format}
      />
    </StatCard>
  );
};

export const IncomeChip = ({ summary }: IncomeCardProps) => {
  const { t } = useTranslation('authenticated.timeTracking');
  const { approved, format } = useIncome(summary);
  const tooltip = useIncomeTooltip(summary);

  return (
    <SummaryChip
      icon={<Wallet className="h-3 w-3" />}
      label={t('approvals.summaryCards.income.title')}
      value={format(approved)}
      tooltip={tooltip}
    />
  );
};
